import { authenticatedInstance } from '../index/index.service';
import type { GroupType, VariantComboGroupView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { GroupType, VariantComboGroupView, VariantComboItemView } from 'shared-models/browser';

export type UpsertVariantComboItemRequest = {
  id?: number;
  name?: string;
  priceWithinFreeLimit: number;
  priceAfterFreeLimit: number;
  isDefault?: boolean | null;
  kds?: boolean | null;
  maxPick?: number | null;
  sortOrder?: number;
  printerIds?: number[];
  fromProductId?: number;
};

export type GroupTreeNodeRequest = {
  id?: number;
  groupType: GroupType;
  name: string;
  pickLimit: number;
  freeLimit?: number;
  minPick?: number;
  sortOrder?: number;
  items: (UpsertVariantComboItemRequest & { childGroups: GroupTreeNodeRequest[] })[];
};

export type UpsertVariantComboGroupRequest = {
  id?: number;
  groupType: GroupType;
  parentProductId?: number | null;
  parentItemId?: number | null;
  name: string;
  pickLimit: number;
  freeLimit: number;
  minPick?: number;
  sortOrder?: number;
  items: UpsertVariantComboItemRequest[];
};

class VariantsCombosService {
  // Summary: Lists a product's top-level variant/combo groups (fully nested) for manager settings.
  async listForProduct(tenantId: string, productId: number, employeeSession: string): Promise<VariantComboGroupView[]> {
    try {
      const response = await authenticatedInstance.get<VariantComboGroupView[]>(
        `/variants-combos/tenant/${tenantId}/product/${productId}`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Batch create/update of one variant/combo group + its items (+ printers) from manager settings.
  async upsertGroup(tenantId: string, payload: UpsertVariantComboGroupRequest, employeeSession: string): Promise<VariantComboGroupView> {
    try {
      const response = await authenticatedInstance.post<VariantComboGroupView>(`/variants-combos/tenant/${tenantId}/group`, payload, {
        headers: { 'x-employee-session': employeeSession },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Atomically replaces a product's whole variants/combos tree in one request (Options Builder save).
  async batchUpsertProductGroups(
    tenantId: string,
    productId: number,
    groups: GroupTreeNodeRequest[],
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.put<void>(
        `/variants-combos/tenant/${tenantId}/product/${productId}/groups`,
        { groups },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Lists every top-level group in the tenant (fully nested) for the "copy from existing" picker.
  async listAllForTenant(tenantId: string, employeeSession: string): Promise<VariantComboGroupView[]> {
    try {
      const response = await authenticatedInstance.get<VariantComboGroupView[]>(
        `/variants-combos/tenant/${tenantId}/groups`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Per-product count of top-level groups (Record<productId, count>) for the ניהול מוצרים options
  async getProductGroupCounts(tenantId: string, employeeSession: string): Promise<Record<number, number>> {
    try {
      const response = await authenticatedInstance.get<Record<number, number>>(
        `/variants-combos/tenant/${tenantId}/group-counts`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async copyGroupToProducts(
    tenantId: string,
    productIds: number[],
    group: GroupTreeNodeRequest,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/variants-combos/tenant/${tenantId}/group/copy-to-products`,
        { productIds, group },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Deletes a tenant-scoped group (items + printers + nested groups cascade).
  async deleteGroup(tenantId: string, groupId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.delete<void>(`/variants-combos/tenant/${tenantId}/group/${groupId}`, {
        headers: { 'x-employee-session': employeeSession },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Replaces the printer routing for a single variant/combo item.
  async setItemPrinters(tenantId: string, itemId: number, printerIds: number[], employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.put<void>(
        `/variants-combos/tenant/${tenantId}/item/${itemId}/printers`,
        { printerIds },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const variantsCombosService = new VariantsCombosService();
